import { default as Checkbox } from 'antd/es/checkbox';
import { default as Input } from 'antd/es/input';
import { default as Modal } from 'antd/es/modal';
import { default as Segmented } from 'antd/es/segmented';
import { default as Select } from 'antd/es/select';
import type { ReactNode } from 'react';
import { Fragment } from 'react';
import { Controller } from 'react-hook-form';

import type { WebhookCreateResponseConflictsItem } from 'api';

import { PrimaryButton } from '@atoms/buttons/PrimaryButton';
import { SecondaryButton } from '@atoms/buttons/SecondaryButton';
import { Spinner } from '@atoms/Spinner/Spinner';

import {
  ADD_WEBHOOK_AUTH_OPTION_TEST_ID,
  ADD_WEBHOOK_BUSINESS_UNIT_OPTION_TEST_ID,
  ADD_WEBHOOK_BUSINESS_UNIT_TEST_ID,
  ADD_WEBHOOK_CANCEL_TEST_ID,
  ADD_WEBHOOK_ENDPOINT_ERROR_TEST_ID,
  ADD_WEBHOOK_ENDPOINT_TEST_ID,
  ADD_WEBHOOK_EVENT_TEST_ID,
  ADD_WEBHOOK_EVENTS_ERROR_COPY,
  ADD_WEBHOOK_EVENTS_ERROR_TEST_ID,
  ADD_WEBHOOK_EVENTS_LOADING_TEST_ID,
  ADD_WEBHOOK_FORMAT_OPTION_TEST_ID,
  ADD_WEBHOOK_FORMAT_SELECT_TEST_ID,
  ADD_WEBHOOK_FORM_ID,
  ADD_WEBHOOK_MODAL_TEST_ID,
  ADD_WEBHOOK_SUBMIT_TEST_ID,
  ADD_WEBHOOK_SUBTITLE,
  ADD_WEBHOOK_TITLE,
  ADD_WEBHOOK_X_AUTH_TEST_ID,
  BUSINESS_UNIT_PLACEHOLDER,
  ENDPOINT_URL_PLACEHOLDER,
  SUBSCRIBE_TO_EVENTS_LABEL,
  WEBHOOK_FORMAT_LABELS,
  X_AUTH_PLACEHOLDER
} from './constants';
import { useAddWebhookForm } from './useAddWebhookForm';

import { AuthKeyOption } from '../AuthKeyOption';
import { BusinessUnitOption } from '../BusinessUnitOption';
import { FieldErrorLine } from '../FieldErrorLine';

type AddWebhookModalProps = {
  open: boolean;
  onClose: () => void;
  onConflicts: (conflicts: WebhookCreateResponseConflictsItem[]) => void;
};

const FieldLabel = ({ children }: { children: ReactNode }) => (
  <label className="mb-0.5 flex gap-0.5 text-xs font-medium text-gray-500">
    {children}
    <span className="text-feedback-error">*</span>
  </label>
);

export const AddWebhookModal = ({
  open,
  onClose,
  onConflicts
}: AddWebhookModalProps) => {
  const {
    form,
    businessUnits,
    authKeys,
    events,
    eventsLoading,
    eventsError,
    submit,
    cancel,
    isSubmitting
  } = useAddWebhookForm({ onClose, onConflicts });
  const {
    control,
    watch,
    formState: { errors, isValid }
  } = form;

  const endpointUrl = watch('endpointUrl') ?? '';
  const endpointError =
    endpointUrl.length > 0 ? errors.endpointUrl?.message : undefined;

  return (
    <Modal
      open={open}
      onCancel={cancel}
      afterClose={() => form.reset()}
      width={560}
      destroyOnHidden
      title={<div className="text-xl font-medium">{ADD_WEBHOOK_TITLE}</div>}
      footer={
        <div className="flex justify-end gap-2">
          <SecondaryButton
            data-testid={ADD_WEBHOOK_CANCEL_TEST_ID}
            onClick={cancel}
          >
            Cancel
          </SecondaryButton>
          <PrimaryButton
            data-testid={ADD_WEBHOOK_SUBMIT_TEST_ID}
            form={ADD_WEBHOOK_FORM_ID}
            htmlType="submit"
            disabled={!isValid || isSubmitting}
            loading={isSubmitting}
          >
            Add webhook
          </PrimaryButton>
        </div>
      }
    >
      <form
        id={ADD_WEBHOOK_FORM_ID}
        onSubmit={submit}
        data-testid={ADD_WEBHOOK_MODAL_TEST_ID}
        className="flex flex-col gap-4"
      >
        <p className="text-sm text-content-secondary">{ADD_WEBHOOK_SUBTITLE}</p>

        <div className="flex flex-col">
          <FieldLabel>Endpoint URL</FieldLabel>
          <Controller
            name="endpointUrl"
            control={control}
            render={({ field }) => (
              <Input
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                data-testid={ADD_WEBHOOK_ENDPOINT_TEST_ID}
                placeholder={ENDPOINT_URL_PLACEHOLDER}
                status={endpointError ? 'error' : undefined}
                className="h-12 rounded-2xl"
              />
            )}
          />
          <FieldErrorLine
            message={endpointError}
            data-testid={ADD_WEBHOOK_ENDPOINT_ERROR_TEST_ID}
          />
        </div>

        <div className="flex flex-col">
          <FieldLabel>Business unit</FieldLabel>
          <Controller
            name="businessUnitUuid"
            control={control}
            render={({ field }) => (
              <Select
                showSearch
                data-testid={ADD_WEBHOOK_BUSINESS_UNIT_TEST_ID}
                placeholder={BUSINESS_UNIT_PLACEHOLDER}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                filterOption={(input, option) =>
                  String(option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={businessUnits.map((unit) => ({
                  value: unit.uuid,
                  label: unit.name
                }))}
                optionRender={(option) => {
                  const unit = businessUnits.find(
                    (item) => item.uuid === option.value
                  );
                  return unit ? (
                    <div
                      data-testid={ADD_WEBHOOK_BUSINESS_UNIT_OPTION_TEST_ID(
                        unit.uuid
                      )}
                    >
                      <BusinessUnitOption {...unit} />
                    </div>
                  ) : null;
                }}
                labelRender={(label) => label.label}
                className="w-full"
              />
            )}
          />
        </div>

        <div className="flex flex-col">
          <FieldLabel>X-Auth-UUID</FieldLabel>
          <Controller
            name="xAuthUuid"
            control={control}
            render={({ field }) => (
              <Select
                showSearch
                data-testid={ADD_WEBHOOK_X_AUTH_TEST_ID}
                placeholder={X_AUTH_PLACEHOLDER}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                filterOption={(input, option) =>
                  `${option?.label ?? ''} ${option?.value ?? ''}`
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={authKeys.map((key) => ({
                  value: key.uuid,
                  label: key.name,
                  disabled: !key.enabled
                }))}
                optionRender={(option) => {
                  const key = authKeys.find(
                    (item) => item.uuid === option.value
                  );
                  return key ? (
                    <div
                      data-testid={ADD_WEBHOOK_AUTH_OPTION_TEST_ID(key.uuid)}
                    >
                      <AuthKeyOption {...key} />
                    </div>
                  ) : null;
                }}
                labelRender={(label) => label.label}
                className="w-full"
              />
            )}
          />
        </div>

        <div className="mt-2 flex flex-col gap-2">
          <p className="flex gap-0.5 text-base font-medium leading-[22px] text-content-primary">
            {SUBSCRIBE_TO_EVENTS_LABEL}
            <span className="text-feedback-error">*</span>
          </p>
          {eventsLoading && (
            <div
              data-testid={ADD_WEBHOOK_EVENTS_LOADING_TEST_ID}
              className="flex justify-center py-6"
            >
              <Spinner />
            </div>
          )}
          {eventsError && !eventsLoading && (
            <div
              data-testid={ADD_WEBHOOK_EVENTS_ERROR_TEST_ID}
              className="flex items-center justify-center py-6 text-content-secondary"
            >
              {ADD_WEBHOOK_EVENTS_ERROR_COPY}
            </div>
          )}
          {!eventsLoading && !eventsError && (
            <Controller
              name="selectedEvents"
              control={control}
              render={({ field }) => {
                const value = field.value ?? [];
                return (
                  <div className="grid grid-cols-[1fr_auto] items-center gap-x-4 gap-y-2">
                    {events.map(({ event, formats }) => (
                      <Fragment key={event}>
                        <Checkbox
                          data-testid={ADD_WEBHOOK_EVENT_TEST_ID(event)}
                          checked={value.includes(event)}
                          onChange={() =>
                            field.onChange(
                              value.includes(event)
                                ? value.filter((item) => item !== event)
                                : [...value, event]
                            )
                          }
                        >
                          <span className="font-mono text-content-primary">
                            {event}
                          </span>
                        </Checkbox>
                        <Controller
                          name={`eventFormats.${event}`}
                          control={control}
                          render={({ field: formatField }) => (
                            <div
                              data-testid={ADD_WEBHOOK_FORMAT_SELECT_TEST_ID(
                                event
                              )}
                            >
                              <Segmented
                                value={formatField.value}
                                onChange={formatField.onChange}
                                disabled={!value.includes(event)}
                                options={formats.map((format) => ({
                                  value: format,
                                  label: (
                                    <span
                                      data-testid={ADD_WEBHOOK_FORMAT_OPTION_TEST_ID(
                                        event,
                                        format
                                      )}
                                    >
                                      {WEBHOOK_FORMAT_LABELS[format]}
                                    </span>
                                  )
                                }))}
                              />
                            </div>
                          )}
                        />
                      </Fragment>
                    ))}
                  </div>
                );
              }}
            />
          )}
        </div>
      </form>
    </Modal>
  );
};
